(ns app.front.view.group.admin.inspections.visit-type
  (:require [app.domain.inspection.model :as inspection-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.ui.action-buttons :as ui-action-buttons]
            [app.front.ui.config-pills :as ui-config-pills]
            [app.front.ui.i18n :as ui-i18n]
            [app.front.ui.select :as ui-select]
            [app.front.ui.table :as ui-table]
            [app.front.view.group.admin.common :as acommon]
            [app.front.view.group.admin.inspections.db :as iadb]
            [app.front.view.group.admin.inspections.subs :as ias]
            [app.front.view.group.admin.subs :as gas]
            [app.i18n.model :as i18n-model]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]))

(rf/reg-sub ::visit-type-in-use
  (fn [db [_ visit-type]]
    (contains? (::iadb/visit-types-in-use db) (:db/id visit-type))))

(rf/reg-event-db ::toggle-row-expansion
  (fn [db [_ id]]
    (let [expanded? (-> db ::expanded-rows (contains? id))]
      (update db ::expanded-rows (comp set (if expanded? disj conj)) id))))

(rf/reg-sub ::expanded-rows
  (fn [db]
    (::expanded-rows db #{})))

(rf/reg-sub ::visit-type-data
  :-> ::visit-type)

(rf/reg-sub ::get-visit-type-data
  :<- [::visit-type-data]
  (fn [data [_ key]]
    (get data key)))

(rf/reg-sub ::get-valid-visit-results
  :<- [::visit-type-data]
  (fn [data _]
    (->> data
         :inspection-visit-type/visit-results
         (filter du/is-active?))))

(rf/reg-sub ::invalid-visit-type-data?
  :<- [::visit-type-data]
  :<- [::gas/group-permit-types]
  (fn [[{:inspection-visit-type/keys [names workflow visit-results]} permit-types]]
    (or (i18n-model/blank? names)
        (and (nil? workflow) (not (contains? permit-types :permit-type/PWO)))
        (empty? visit-results))))

(rf/reg-sub ::no-edit-changes?
  (fn [[_ id]]
    [(rf/subscribe [::entities/entity id])
     (rf/subscribe [::visit-type-data])])
  (fn [[entity edited]]
    (= (-> (select-keys entity inspection-model/visit-type-editable-keys)
           (update :inspection-visit-type/names i18n-model/strings-map))
       (select-keys edited inspection-model/visit-type-editable-keys))))

(rf/reg-event-db ::set-visit-type-field
  (fn [db [_ key data]]
    (assoc-in db [::visit-type key] data)))

(rf/reg-event-db ::set-create-open
  (fn [db [_ value]]
    (assoc db ::create-open? value)))

(rf/reg-sub ::creating-new-visit-type?
  :-> ::create-open?)

(rf/reg-sub ::editing-visit-type?
  :<- [::visit-type-data]
  :-> some?)

(rf/reg-event-db ::set-visit-type
  (fn [db [_ {id :db/id :as visit-type}]]
    (cond-> (assoc db ::visit-type (some-> visit-type
                                           (select-keys (conj inspection-model/visit-type-editable-keys :db/id))
                                           (update :inspection-visit-type/names i18n-model/strings-map)))
      id (update ::expanded-rows conj id))))

(defn save-data
  ([db]
   (save-data db false))
  ([db is-pwo?]
   (cond-> (::visit-type db)
     is-pwo? (assoc :inspection-visit-type/workflow :inspection.visit.type-workflow/general-inspection)
     true    (select-keys [:inspection-visit-type/names
                           :inspection-visit-type/workflow
                           :inspection-visit-type/visit-results])
     true    (update :inspection-visit-type/visit-results du/entity-id-set))))

(rf/reg-event-fx ::create-visit-type
  (fn [{:keys [db]} [_ authority-group-id is-pwo?]]
    {:comm/command {:command    :inspection/create-visit-type
                    :method     :post
                    :data       (util/mmap {:data (save-data db is-pwo?)}
                                           authority-group-id)
                    :on-success [::save-visit-type.success]
                    :notify     {:success :inspection-visit-type/created}}}))

(rf/reg-event-fx ::edit-visit-type
  (fn [{:keys [db]} _]
    {:comm/command {:command    :inspection/edit-visit-type
                    :method     :post
                    :data       {:data (save-data db)
                                 :id   (-> db ::visit-type :db/id)}
                    :on-success [::save-visit-type.success]
                    :notify     {:success :inspection-visit-type/saved}}}))

(rf/reg-event-fx ::delete-visit-type
  (fn [_ [_ {id :db/id}]]
    {:comm/command {:command    :inspection/delete-visit-type
                    :method     :post
                    :data       {:id id}
                    :on-success [::entities/apply-changes]
                    :notify     {:success :inspection-visit-type/deleted}}}))

(rf/reg-event-fx ::save-visit-type.success
  (fn [_ [_ status resp]]
    {:fx [[:dispatch [::entities/apply-changes status resp]]
          [:dispatch [::set-create-open false]]
          [:dispatch [::set-visit-type nil]]]}))

(defn- name-field [tr show-label?]
  (let [strings (<sub [::get-visit-type-data :inspection-visit-type/names])]
    [ui-i18n/localized-string-edit
     {:id                  "visit-type-name"
      :type                :text
      :label-text          (when show-label? (tr [:inspection-visit-type/names]))
      :light               true
      :required            show-label?
      :strings             strings
      :on-change           #(rf/dispatch-sync [::set-visit-type-field :inspection-visit-type/names %])
      :allowed-locales-sub ::gas/allowed-locales}]))

(defn- name-col [visit-type]
  (let [lang (ui-i18n/get-actual-language-to-edit
               (-> visit-type :inspection-visit-type/names i18n-model/strings-map)
               (<sub [::ui-i18n/editor-language ::gas/allowed-locales]))]
    (inspection-model/visit-type-name visit-type lang)))

(defn- results-selector [{:keys [tr authority-group-id]} id disabled selected-results]
  (let [lang           (<sub [:language])
        all-results    (<sub [::ias/visit-result-entities authority-group-id])
        edited-results (<sub [::get-valid-visit-results])
        sort-results   (fn [results] (sort-by :inspection-visit-result/names (i18n-model/localized-string-comparator lang) results))]
    [ui-config-pills/config-pills
     (util/mmap {:id                (str "results-selector-" id)
                 :legend-text       (tr [:inspection-visit-type/visit-results])
                 :empty-placeholder :inspection-visit-type/no-visit-results
                 :val->str          #(inspection-model/visit-result-name % lang)
                 :values            all-results
                 :add-item          (when (not disabled) #(>evt [::set-visit-type-field :inspection-visit-type/visit-results (conj edited-results %)]))
                 :remove-item       #(>evt [::set-visit-type-field :inspection-visit-type/visit-results (remove (comp (partial = (:db/id %)) :db/id) edited-results)])
                 :selected          (when (not disabled) (sort-results edited-results))
                 :pre-selected      (when disabled (sort-results selected-results))
                 :add-button-text   (tr [:inspection-visit-type/add-visit-result])
                 :required?         (not disabled)}
                tr)]))

(defn- workflow-selector [tr disabled? show-label?]
  (let [value   (<sub [::get-visit-type-data :inspection-visit-type/workflow])
        is-pwo? (contains? (<sub [::gas/group-permit-types]) :permit-type/PWO)]
    (when (or (not is-pwo?) (not show-label?))
      [:div.flex.flex-row.items-end.gap-x-2
       [:div.flex-col
        [ui-select/select {:values       inspection-model/visit-type-workflows
                           :allow-empty? false
                           :required     true
                           :disabled     (or disabled? is-pwo?)
                           :val->str     #(tr [%])
                           :label-text   (when show-label? (tr [:inspection-visit-type/workflow]))
                           :id           "visit-type-workflow"
                           :on-change    #(>evt [::set-visit-type-field :inspection-visit-type/workflow %])
                           :value        value}]]])))

(defn- workflow-col [tr {:inspection-visit-type/keys [workflow]}]
  [:div.flex.flex-row.space-between.items-end.gap-x-2
   [:div (tr [workflow])]])

(defn- visit-type-editor [{:keys [tr authority-group-id] :as ctx}]
  (let [is-pwo? (contains? (<sub [::gas/group-permit-types]) :permit-type/PWO)]
    [:div.flex.flex-col.p-4.gap-y-2.border.border-solid.border-blue-600.bg-gray-200
     [name-field tr true]
     [workflow-selector tr false true]
     [results-selector ctx "new" false nil]
     [:div.flex.justify-end
      [ui-action-buttons/save-cancel-buttons
       (util/mmap {:on-cancel      (fn []
                                     (>evt [::set-create-open false])
                                     (>evt [::set-visit-type nil]))
                   :on-save        #(>evt [::create-visit-type authority-group-id is-pwo?])
                   :save-disabled? (<sub [::invalid-visit-type-data?])}
                  tr)]]]))

(defn- expanded-visit-type [{:keys [tr] :as ctx} editing-this? {id :db/id :as visit-type}]
  (let [editing-this? (editing-this? visit-type)
        results       (when (not editing-this?)
                        (->> visit-type
                             :inspection-visit-type/visit-results
                             (filter du/is-active?)))
        no-changes?   (<sub [::no-edit-changes? id])
        invalid-data? (<sub [::invalid-visit-type-data?])]
    [:div
     [results-selector ctx id (not editing-this?) results]
     (when editing-this?
       [:div.pt-2.flex.justify-end
        [ui-action-buttons/save-cancel-buttons
         {:on-cancel      #(>evt [::set-visit-type nil])
          :on-save        #(>evt [::edit-visit-type])
          :save-disabled? (or no-changes? invalid-data?)
          :size           :small
          :tr             tr}]])]))

(defn- table-actions [tr visit-type]
  (let [in-use? (<sub [::visit-type-in-use visit-type])]
    [{:title    (tr [:edit])
      :icon     i/pen
      :on-click #(>evt [::set-visit-type visit-type])}
     (when (not in-use?)
       {:title           (tr [:delete])
        :icon            i/trash
        :on-click        #(>evt [::delete-visit-type visit-type])
        :confirm?        true
        :confirm-content (tr [:inspection-visit-type/delete-confirm])})]))

(defn- visit-type-table [{:keys [tr authority-group-id] :as ctx}]
  (let [visit-types   (<sub [::ias/visit-type-entities authority-group-id])
        editing?      (<sub [::editing-visit-type?])
        edited-id     (<sub [::get-visit-type-data :db/id])
        editing-this? (fn [{id :db/id}] (= id edited-id))
        expanded-rows (<sub [::expanded-rows])
        creating-new? (<sub [::creating-new-visit-type?])]
    [ui-table/modern-responsive-table
     {:cols              [[:inspection-visit-type/names
                           (fn [visit-type]
                             (if (editing-this? visit-type)
                               [name-field tr false]
                               [name-col visit-type]))]
                          [:inspection-visit-type/workflow
                           (fn [visit-type]
                             (let [in-use? (<sub [::visit-type-in-use visit-type])]
                               (if (editing-this? visit-type)
                                 [workflow-selector tr in-use? false]
                                 [workflow-col tr visit-type])))]]
      :expanded-func     (partial expanded-visit-type ctx editing-this?)
      :expanded-row-keys expanded-rows
      :on-expand         #(>evt [::toggle-row-expansion %])
      :tr                tr
      :items             visit-types
      :make-actions      (when (and (not editing?)
                                    (not creating-new?))
                           (partial table-actions tr))}]))

(defn view [{:keys [tr] :as ctx}]
  (let [creating-new? (<sub [::creating-new-visit-type?])
        editing?      (<sub [::editing-visit-type?])]
    [acommon/section
     {}
     [:div.flex.flex-col.gap-y-4
      [ui-i18n/edit-language-selector {:allowed-locales-sub ::gas/allowed-locales}]
      [visit-type-table ctx]
      (if creating-new?
        [visit-type-editor ctx]
        (cc/button {:kind             :primary
                    :size             :small
                    :icon             i/plus
                    :icon-description ""
                    :on-click         #(>evt [::set-create-open true])
                    :disabled         editing?}
                   (tr [:inspection-visit-type/add-new])))]]))
